import { ColDef, ColGroupDef } from '@ag-grid-community/core';
import dayjs from 'dayjs';

import {
  AgBudgetAttributeComponentParams,
  AgBudgetGroupHeaderComponent,
} from '@features/ag-budget-group-header/ag-budget-group-header.component';

import { AgRawTooltip } from '@shared/components/ag-raw-tooltip/ag-raw-tooltip';
import { TableConstants } from '@shared/constants/table.constants';
import { Currency } from '@shared/services/gql.service';
import { Utils } from '@shared/utils/utils';

import { SiteSummaryGridContext } from '../types/site-summary-grid-context';
import { SiteSummaryGridRow } from '../types/site-summary-grid-row';

const MAX_WIDTH = 400;

const getColumnWith = (width: number, maxWidth = MAX_WIDTH): ColDef<SiteSummaryGridRow> => {
  return {
    maxWidth,
    minWidth: width,
    width,
  };
};

const commonColumnConfig: ColDef<SiteSummaryGridRow> = {
  cellClass: [TableConstants.STYLE_CLASSES.CELL_ALIGN_LEFT],
  pinned: 'left',
  suppressSpanHeaderHeight: true,
  tooltipComponent: AgRawTooltip,
  tooltipValueGetter: (params) => params.value?.toString() || '',
  valueFormatter: (params) => params.value || Utils.zeroHyphen,
};

export const siteCostSummaryTableSiteColumns = (
  ctx: SiteSummaryGridContext
): ColGroupDef<SiteSummaryGridRow> => {
  return {
    children: [
      siteNoColumn,
      siteNameColumn,
      siteBudgetVersionNameColumn,
      effectiveDateColumn,
      totalAmountColumn(ctx),
      ...(ctx.isSelectedCurrencyContracted && ctx.isAnySiteHasNonUsdCurrency && ctx.isSiteExpanded
        ? [contractedCurrencyColumn]
        : []),
    ],
    headerClass: ['ag-gray-header'],
    headerGroupComponent: AgBudgetGroupHeaderComponent,
    headerGroupComponentParams: <AgBudgetAttributeComponentParams>{
      afterAttrToggle: (_, expand) => {
        ctx.afterSitesExpandToggle(!!expand);
      },
      columnsToCollapse: ['siteName', 'siteBudgetVersionName', 'contractedCurrency'],
      expandLevel: () => 1,
      hideExpandAll: true,
      localStorageKey: 'site-cost-summary-table-site-columns',
      template: 'Sites',
    },
  };
};

const siteNoColumn: ColDef<SiteSummaryGridRow> = {
  ...getColumnWith(64, 150),
  ...commonColumnConfig,
  field: 'siteNo',
  headerName: '#',
};

const siteNameColumn: ColDef<SiteSummaryGridRow> = {
  ...getColumnWith(129),
  ...commonColumnConfig,
  field: 'siteName',
  headerName: 'Site Name',
};

const siteBudgetVersionNameColumn: ColDef<SiteSummaryGridRow> = {
  ...getColumnWith(135),
  ...commonColumnConfig,
  field: 'siteBudgetVersionName',
  headerName: 'Site Budget Version',
};

const effectiveDateColumn: ColDef<SiteSummaryGridRow> = {
  ...getColumnWith(124, 124),
  ...commonColumnConfig,
  field: 'effectiveDate',
  headerName: 'Effective Date',
  valueFormatter: (params) => {
    if (!params.value) return Utils.zeroHyphen;

    return dayjs(params.value).format('MM/DD/YYYY');
  },
};

const contractedCurrencyColumn: ColDef<SiteSummaryGridRow> = {
  ...getColumnWith(89, 89),
  ...commonColumnConfig,
  field: 'contractedCurrency',
  headerName: 'Currency',
};

const totalAmountColumn = (ctx: SiteSummaryGridContext): ColDef<SiteSummaryGridRow> => ({
  ...getColumnWith(162, 400),
  ...commonColumnConfig,
  cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
  headerName: 'Total',
  valueFormatter: (params) =>
    Utils.agCurrencyFormatter(
      params,
      ctx.isSelectedCurrencyContracted ? params.data?.contractedCurrency : Currency.USD
    ),
  valueGetter: (params) => {
    return ctx.isSelectedCurrencyContracted
      ? params.data?.totalAmountContracted
      : params.data?.totalAmount;
  },
});
